import {
  ApplicationConfig,
  DOCUMENT,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  RouteReuseStrategy,
  Router,
  withDisabledInitialNavigation,
  withNavigationErrorHandler,
} from '@angular/router';
import {routes} from './app.routes';
import {LpAuthRouteReuseStrategy} from '@/app/core/routing/lp-auth-route-reuse.strategy';
import {
  clearChunkReloadFlag,
  reloadOnChunkLoadError,
} from '@/app/core/utils/chunk-load-error.util';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {authApiInterceptor} from '@/app/core/interceptors/auth-api.interceptor';
import {loadingInterceptor} from '@/app/core/interceptors/loading.interceptor';
import {apiErrorInterceptor} from '@/app/core/interceptors/api-error.interceptor';

import {provideAnimations} from '@angular/platform-browser/animations';
import {providePrimeNG} from 'primeng/config';
import {ToastModule} from 'primeng/toast';
import {MuwaridPreset} from '@/app/styles/muwarid-preset';
import {ConfirmationService, MessageService} from 'primeng/api';
import {provideTranslateService, TranslateService} from '@ngx-translate/core';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {provideStore} from '@ngrx/store';
import {reducers} from '@/stores';
import {layoutStore} from '@/app/layout/store/layout.store';
import {firstValueFrom} from 'rxjs';
import {provideEchartsCore} from 'ngx-echarts';
import {unregisterAngularServiceWorker} from '@/app/core/utils/unregister-ngsw.util';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({eventCoalescing: true}),
    provideRouter(
      routes,
      withDisabledInitialNavigation(),
      withNavigationErrorHandler((error) => {
        reloadOnChunkLoadError(error.error ?? error);
        throw error;
      }),
    ),
    {provide: RouteReuseStrategy, useClass: LpAuthRouteReuseStrategy},
    provideEchartsCore({
      echarts: () => import('echarts'),
    }),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        loadingInterceptor,
        authApiInterceptor,
        apiErrorInterceptor,
      ]),
    ),
    provideStore(reducers),
    provideAnimations(),
    importProvidersFrom(ToastModule),
    providePrimeNG({
      theme: {
        preset: MuwaridPreset,
        options: {darkModeSelector: '.dark'},
      },
    }),
    MessageService,
    ConfirmationService,
    provideTranslateService({fallbackLang: 'en'}),
    ...provideTranslateHttpLoader({prefix: '/i18n/', suffix: '.json'}),
    provideAppInitializer(() => {
      clearChunkReloadFlag();
      const translate = inject(TranslateService);
      return Promise.all([
        fetch('/i18n/en.json').then((r) => r.json()),
        fetch('/i18n/ar.json').then((r) => r.json()),
      ]).then(([en, ar]) => {
        translate.setTranslation('en', en);
        translate.setTranslation('ar', ar);
        translate.setFallbackLang('en');
        const lang = layoutStore.language();
        return firstValueFrom(translate.use(lang));
      });
    }),

    provideAppInitializer(() => {
      const router = inject(Router);
      const doc = inject(DOCUMENT);
      const match = doc.cookie.match(/(?:^|; )TenantName=([^;]*)/);
      const tenant = match ? decodeURIComponent(match[1]) : null;
      const path = doc.location?.pathname ?? '/';
      if (tenant && (path === '/' || path === '')) {
        return router.navigateByUrl(`/lp/${tenant}`, {
          skipLocationChange: true,
        });
      }
      return router.initialNavigation();
    }),
    provideAppInitializer(() => unregisterAngularServiceWorker()),
  ],
};
